import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import TopBar from "@/components/TopBar";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { TID } from "@/constants/testIds";
import { Trash2, UserPlus, Tag as TagIcon, Users as UsersIcon, Loader2, Edit3, Check, X, ShieldCheck, Clock, Folder as FolderIcon, KeyRound, ChevronDown, ChevronRight, Send, Ban, RotateCcw, KeyRound as KeyIcon, Palette } from "lucide-react";
import { toast } from "sonner";
import { BRANDS } from "@/brands";

export default function Admin() {
  const { user } = useAuth();
  const [folders, setFolders] = useState([]);
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [scopedTags, setScopedTags] = useState([]); // [{scope_folder_id, scope_folder_name, tags:[]}]
  const [audit, setAudit] = useState([]);
  const [view, setView] = useState("grid");

  // invite form (team users)
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [pw, setPw] = useState("");
  const [role, setRole] = useState("viewer");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // client-user invite form
  const [cEmail, setCEmail] = useState("");
  const [cName, setCName] = useState("");
  const [cPw, setCPw] = useState("");
  const [cScope, setCScope] = useState("");
  const [cBusy, setCBusy] = useState(false);
  const [cErr, setCErr] = useState("");

  useEffect(() => {
    api.get("/folders").then((r) => setFolders(r.data));
  }, []);

  useEffect(() => {
    if (tab === "users") api.get("/users").then((r) => setUsers(r.data));
    if (tab === "clients") api.get("/admin/clients").then((r) => setClients(r.data));
    if (tab === "tags") api.get("/admin/tags").then((r) => setScopedTags(r.data));
    if (tab === "audit") api.get("/admin/audit").then((r) => setAudit(r.data));
  }, [tab]);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const rootFolders = folders.filter((f) => !f.parent_id);

  const invite = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post("/users", { email, name, password: pw, role });
      setEmail(""); setName(""); setPw(""); setRole("viewer");
      const r = await api.get("/users");
      setUsers(r.data);
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Failed to create user");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this user?")) return;
    await api.delete(`/users/${id}`);
    const r = await api.get("/users");
    setUsers(r.data);
  };

  const inviteClient = async (e) => {
    e.preventDefault();
    setCErr(""); setCBusy(true);
    try {
      await api.post("/admin/clients", { email: cEmail, name: cName, password: cPw, scope_folder_id: cScope });
      setCEmail(""); setCName(""); setCPw(""); setCScope("");
      const r = await api.get("/admin/clients");
      setClients(r.data);
    } catch (ex) {
      setCErr(ex.response?.data?.detail || "Failed to create client");
    } finally {
      setCBusy(false);
    }
  };

  const removeClient = async (id) => {
    if (!confirm("Delete this client user? Their scope folder and its content stay intact.")) return;
    await api.delete(`/admin/clients/${id}`);
    const r = await api.get("/admin/clients");
    setClients(r.data);
  };

  return (
    <div className="flex min-h-screen grain-overlay">
      <Sidebar folders={folders} onRefresh={() => api.get("/folders").then((r) => setFolders(r.data))} />
      <div className="flex-1 flex flex-col min-w-0">
        <TopBar view={view} setView={setView} onSearch={() => {}} searchValue="" onUpload={() => {}} canUpload={false} />

        <main className="flex-1 px-4 sm:px-6 md:px-10 lg:px-12 py-6 md:py-8 pb-20">
          <div className="mb-8">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Admin</div>
            <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">Manage</h1>
            <p className="text-sm text-zinc-500 mt-1">Users, roles, and tag taxonomy</p>
          </div>

          <div className="flex gap-1 border-b border-zinc-800 mb-8 overflow-x-auto whitespace-nowrap -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabButton testid={TID.adminUsersTab} active={tab === "users"} onClick={() => setTab("users")} icon={UsersIcon}>Team</TabButton>
            <TabButton testid="admin-clients-tab" active={tab === "clients"} onClick={() => setTab("clients")} icon={KeyRound}>Clients</TabButton>
            <TabButton testid={TID.adminTagsTab} active={tab === "tags"} onClick={() => setTab("tags")} icon={TagIcon}>Tags</TabButton>
            <TabButton testid="admin-audit-tab" active={tab === "audit"} onClick={() => setTab("audit")} icon={ShieldCheck}>Audit log</TabButton>
          </div>

          {tab === "users" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 min-w-0">
              <form onSubmit={invite} className="md:col-span-1 bg-zinc-950/50 border border-zinc-800/60 rounded-xl p-4 sm:p-6 space-y-4 h-fit min-w-0">
                <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
                  <UserPlus className="w-4 h-4 text-[var(--brand-accent)]" /> Invite user
                </h2>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500">Name</label>
                  <input data-testid={TID.inviteName} value={name} onChange={(e) => setName(e.target.value)} required className="w-full mt-1 bg-black/40 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-[var(--brand-accent)]/60" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500">Email</label>
                  <input data-testid={TID.inviteEmail} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full mt-1 bg-black/40 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-[var(--brand-accent)]/60" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500">Temporary password</label>
                  <input data-testid={TID.invitePassword} type="text" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={6} className="w-full mt-1 bg-black/40 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-[var(--brand-accent)]/60" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500">Role</label>
                  <select data-testid={TID.inviteRole} value={role} onChange={(e) => setRole(e.target.value)} className="w-full mt-1 bg-black/40 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-[var(--brand-accent)]/60">
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                {err && <div className="text-xs text-red-400">{err}</div>}
                <button data-testid={TID.inviteSubmit} type="submit" disabled={busy} className="w-full bg-[var(--brand-accent)] text-black font-semibold rounded-md py-2 text-sm hover:bg-[var(--brand-accent)]/90 transition flex items-center justify-center gap-2 disabled:opacity-60">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />} Invite
                </button>
              </form>

              <div className="md:col-span-2">
                <h2 className="font-heading font-semibold text-lg mb-3">Team members ({users.length})</h2>
                <div className="border border-zinc-800/60 rounded-xl overflow-hidden">
                  {users.map((u) => (
                    <div key={u.id} className="flex items-center gap-4 px-4 py-3 border-b border-zinc-900 last:border-0 hover:bg-zinc-900/30">
                      <div className="w-9 h-9 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center text-xs font-medium">
                        {u.name?.[0]?.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-zinc-200 truncate">{u.name}</div>
                        <div className="text-xs text-zinc-500 truncate">{u.email}</div>
                      </div>
                      <StatusBadge status={u.status || "active"} />
                      <div className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border border-zinc-800 text-zinc-400">
                        {u.role}
                      </div>
                      <BrandSelect
                        userId={u.id}
                        currentBrand={u.brand}
                        onChanged={async () => { const r = await api.get("/users"); setUsers(r.data); }}
                      />
                      <StatusActions
                        user={u}
                        basePath="/users"
                        isSelf={u.id === user?.id}
                        onChanged={async () => { const r = await api.get("/users"); setUsers(r.data); }}
                        onDelete={() => remove(u.id)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === "clients" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 min-w-0">
              <form onSubmit={inviteClient} className="md:col-span-1 bg-zinc-950/50 border border-zinc-800/60 rounded-xl p-4 sm:p-6 space-y-4 h-fit min-w-0">
                <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
                  <KeyRound className="w-4 h-4 text-[var(--brand-accent)]" /> Add client
                </h2>
                <p className="text-[11px] text-zinc-500 leading-relaxed">
                  A client user logs in with email + password and is <strong className="text-zinc-300">locked to one root folder</strong> — its tags, videos, and subfolders. They see nothing else.
                </p>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500">Name</label>
                  <input data-testid="client-invite-name" value={cName} onChange={(e) => setCName(e.target.value)} required className="w-full mt-1 bg-black/40 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-[var(--brand-accent)]/60" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500">Email</label>
                  <input data-testid="client-invite-email" type="email" value={cEmail} onChange={(e) => setCEmail(e.target.value)} required className="w-full mt-1 bg-black/40 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-[var(--brand-accent)]/60" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500">Temporary password</label>
                  <input data-testid="client-invite-password" type="text" value={cPw} onChange={(e) => setCPw(e.target.value)} required minLength={6} className="w-full mt-1 bg-black/40 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-[var(--brand-accent)]/60" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500">Scope folder</label>
                  <select data-testid="client-invite-scope" value={cScope} onChange={(e) => setCScope(e.target.value)} required className="w-full mt-1 bg-black/40 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-[var(--brand-accent)]/60">
                    <option value="">— select root folder —</option>
                    {rootFolders.map((f) => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                </div>
                {cErr && <div className="text-xs text-red-400">{cErr}</div>}
                <button data-testid="client-invite-submit" type="submit" disabled={cBusy || !cScope} className="w-full bg-[var(--brand-accent)] text-black font-semibold rounded-md py-2 text-sm hover:bg-[var(--brand-accent)]/90 transition flex items-center justify-center gap-2 disabled:opacity-60">
                  {cBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />} Create client
                </button>
              </form>

              <div className="md:col-span-2">
                <h2 className="font-heading font-semibold text-lg mb-3">Clients ({clients.length})</h2>
                <div className="border border-zinc-800/60 rounded-xl overflow-hidden">
                  {clients.length === 0 && (
                    <div className="py-10 text-center text-sm text-zinc-500">
                      No clients yet. Add one on the left to give a specific reviewer access to just their folder.
                    </div>
                  )}
                  {clients.map((c) => {
                    const scopeName = folders.find((f) => f.id === c.client_scope_folder_id)?.name || c.client_scope_folder_id?.slice(0, 8) || "—";
                    return (
                      <div key={c.id} className="flex items-center gap-4 px-4 py-3 border-b border-zinc-900 last:border-0 hover:bg-zinc-900/30">
                        <div className="w-9 h-9 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center text-xs font-medium">
                          {c.name?.[0]?.toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-zinc-200 truncate">{c.name}</div>
                          <div className="text-xs text-zinc-500 truncate">{c.email}</div>
                        </div>
                        <div className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border border-[var(--brand-accent)]/40 text-[var(--brand-accent)]/90 flex items-center gap-1.5">
                          <FolderIcon className="w-3 h-3" /> {scopeName}
                        </div>
                        <StatusBadge status={c.status || "active"} />
                        <BrandSelect
                          userId={c.id}
                          currentBrand={c.brand}
                          onChanged={async () => { const r = await api.get("/admin/clients"); setClients(r.data); }}
                        />
                        <StatusActions
                          user={c}
                          basePath="/admin/clients"
                          onChanged={async () => { const r = await api.get("/admin/clients"); setClients(r.data); }}
                          onDelete={() => removeClient(c.id)}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {tab === "tags" && (
            <div>
              <div className="mb-4">
                <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
                  <TagIcon className="w-4 h-4 text-[var(--brand-accent)]" /> Tags across every client scope
                </h2>
                <p className="text-xs text-zinc-500 mt-1">Each root folder is its own namespace. Renaming a tag here only touches videos inside that scope.</p>
              </div>
              {scopedTags.length === 0 ? (
                <div className="py-12 text-center text-zinc-500 text-sm">
                  No tags yet. Tags appear here automatically once you tag videos.
                </div>
              ) : (
                <div className="space-y-4">
                  {scopedTags.map((s) => (
                    <ScopeSection
                      key={s.scope_folder_id}
                      scope={s}
                      onChanged={async () => { const r = await api.get("/admin/tags"); setScopedTags(r.data); }}
                    />
                  ))}
                </div>
              )}
              <p className="mt-6 text-[10px] uppercase tracking-wider text-zinc-600">
                Tip: renaming to an existing tag inside the same scope will merge them.
              </p>
            </div>
          )}

          {tab === "audit" && (
            <div>
              <h2 className="font-heading font-semibold text-lg mb-1 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[var(--brand-accent)]" /> Audit log
              </h2>
              <p className="text-xs text-zinc-500 mb-4">Most recent {audit.length} destructive admin actions. For internal accountability.</p>
              {audit.length === 0 ? (
                <div className="py-12 text-center text-zinc-500 text-sm">No audit events yet.</div>
              ) : (
                <div className="space-y-1">
                  {audit.map((a) => <AuditRow key={a.id} a={a} />)}
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 transition ${active ? "border-[var(--brand-accent)] text-[var(--brand-accent)]" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
    >
      <Icon className="w-3.5 h-3.5" /> {children}
    </button>
  );
}

function StatusBadge({ status }) {
  const styles = {
    pending:  { bg: "bg-amber-500/10",  border: "border-amber-500/40",  text: "text-amber-300",  label: "Pending"  },
    active:   { bg: "bg-emerald-500/10", border: "border-emerald-500/40", text: "text-emerald-300", label: "Active"  },
    revoked:  { bg: "bg-red-500/10",     border: "border-red-500/40",     text: "text-red-300",     label: "Revoked" },
  };
  const s = styles[status] || styles.active;
  return (
    <span data-testid={`status-badge-${status}`} className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${s.bg} ${s.border} ${s.text} font-semibold`}>
      {s.label}
    </span>
  );
}

function StatusActions({ user, basePath, isSelf, onChanged, onDelete }) {
  const [busy, setBusy] = useState("");
  const status = user.status || "active";

  const call = async (verb, path, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(verb);
    try {
      await api.post(`${basePath}/${user.id}/${path}`);
      toast.success({
        resend: `Invite re-sent to ${user.email}`,
        revoke: `Access revoked for ${user.email}`,
        reactivate: `${user.email} reactivated`,
        "reset-password": `Fresh temporary password emailed to ${user.email}`,
      }[verb] || "Done");
      await onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="flex items-center gap-1">
      {status === "pending" && (
        <button data-testid={`resend-invite-${user.id}`} onClick={() => call("resend", "resend-invite")} disabled={!!busy} title="Send a fresh invite email with a new temporary password" className="p-1.5 rounded text-zinc-500 hover:text-[var(--brand-accent)] hover:bg-zinc-900 transition disabled:opacity-40">
          {busy === "resend" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        </button>
      )}
      {status !== "pending" && !isSelf && (
        <button data-testid={`reset-password-${user.id}`} onClick={() => call("reset-password", "reset-password", `Send a fresh temporary password to ${user.email}? Their current password stops working immediately.`)} disabled={!!busy} title="Generate + email a fresh temporary password (works for locked-out users)" className="p-1.5 rounded text-zinc-500 hover:text-[var(--brand-accent)] hover:bg-zinc-900 transition disabled:opacity-40">
          {busy === "reset-password" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyIcon className="w-3.5 h-3.5" />}
        </button>
      )}
      {(status === "pending" || status === "active") && !isSelf && (
        <button data-testid={`revoke-${user.id}`} onClick={() => call("revoke", "revoke", `Revoke access for ${user.email}? Their session will end immediately.`)} disabled={!!busy} title="Kill sessions and block future logins" className="p-1.5 rounded text-zinc-500 hover:text-red-400 hover:bg-zinc-900 transition disabled:opacity-40">
          {busy === "revoke" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
        </button>
      )}
      {status === "revoked" && (
        <button data-testid={`reactivate-${user.id}`} onClick={() => call("reactivate", "reactivate")} disabled={!!busy} title="Restore access" className="p-1.5 rounded text-zinc-500 hover:text-emerald-400 hover:bg-zinc-900 transition disabled:opacity-40">
          {busy === "reactivate" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
        </button>
      )}
      {!isSelf && (
        <button data-testid={`delete-${user.id}`} onClick={onDelete} title="Delete permanently" className="p-1.5 rounded text-zinc-600 hover:text-red-400 hover:bg-zinc-900 transition">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

function ScopeSection({ scope, onChanged }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border border-zinc-800/60 rounded-xl overflow-hidden bg-zinc-950/30">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`scope-toggle-${scope.scope_folder_id}`}
        className="w-full flex items-center gap-3 px-4 py-3 bg-zinc-900/40 hover:bg-zinc-900/60 transition text-left"
      >
        {open ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
        <FolderIcon className="w-4 h-4 text-[var(--brand-accent)]" />
        <span className="font-heading font-semibold text-sm text-zinc-100 flex-1">{scope.scope_folder_name}</span>
        <span className="text-[10px] uppercase tracking-wider text-zinc-500">{scope.tags.length} {scope.tags.length === 1 ? "tag" : "tags"}</span>
      </button>
      {open && (
        <div className="p-3 space-y-1.5">
          {scope.tags.length === 0 ? (
            <div className="text-center text-xs text-zinc-600 py-4">No tags in this scope yet</div>
          ) : (
            scope.tags.map((t) => (
              <TagRow key={t.name} tag={t} scopeId={scope.scope_folder_id} onRenamed={onChanged} onDeleted={onChanged} />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function TagRow({ tag, scopeId, onRenamed, onDeleted }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(tag.name);
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e?.preventDefault();
    const target = val.trim().toLowerCase();
    if (!target || target === tag.name) { setEditing(false); return; }
    setBusy(true);
    try {
      // Scope-aware: hit admin/tags/rename when we have a scope; falls back to
      // the flat /tags/rename for legacy callers.
      if (scopeId) {
        await api.post("/admin/tags/rename", { old: tag.name, new: target, scope_folder_id: scopeId });
      } else {
        await api.post("/tags/rename", { old: tag.name, new: target });
      }
      setEditing(false);
      await onRenamed?.();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not rename tag");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove tag "${tag.name}" from all videos in this scope? Videos themselves are not deleted.`)) return;
    setBusy(true);
    try {
      if (scopeId) {
        await api.post("/admin/tags/delete", { name: tag.name, scope_folder_id: scopeId });
      } else {
        await api.delete(`/tags/${encodeURIComponent(tag.name)}`);
      }
      await onDeleted?.();
    } finally { setBusy(false); }
  };

  return (
    <div className="group flex items-center gap-3 px-3 py-2 bg-zinc-950/40 border border-zinc-800/60 rounded-md hover:border-zinc-700 transition">
      {editing ? (
        <form onSubmit={save} className="flex items-center gap-2 flex-1">
          <input
            data-testid={TID.tagRenameNew}
            autoFocus
            value={val}
            onChange={(e) => setVal(e.target.value)}
            className="flex-1 bg-black/40 border border-zinc-800 rounded px-2 py-1 text-xs focus:outline-none focus:border-[var(--brand-accent)]/60"
          />
          <button data-testid={TID.tagRenameSubmit} type="submit" disabled={busy} className="text-[var(--brand-accent)] hover:text-[var(--brand-accent)]/80">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          </button>
          <button type="button" onClick={() => { setEditing(false); setVal(tag.name); }} className="text-zinc-500 hover:text-zinc-300">
            <X className="w-3.5 h-3.5" />
          </button>
        </form>
      ) : (
        <>
          <span className="flex-1 text-sm text-zinc-200">{tag.name}</span>
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">{tag.count} {tag.count === 1 ? "video" : "videos"}</span>
          <button
            data-testid={TID.tagRenameBtn(tag.name)}
            onClick={() => setEditing(true)}
            className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-[var(--brand-accent)] transition"
            title="Rename or merge"
          >
            <Edit3 className="w-3.5 h-3.5" />
          </button>
          <button
            data-testid={TID.tagDeleteBtn(tag.name)}
            onClick={remove}
            disabled={busy}
            className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-red-400 transition"
            title="Delete tag"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </>
      )}
    </div>
  );
}

function AuditRow({ a }) {
  const actionColors = {
    "user.delete": "text-red-400",
    "folder.delete": "text-red-400",
    "asset.delete": "text-red-400",
    "share.delete": "text-amber-400",
    "comment.delete": "text-amber-400",
    "tag.rename": "text-zinc-300",
    "tag.delete": "text-amber-400",
    "collection.delete": "text-amber-400",
  };
  const color = actionColors[a.action] || "text-zinc-300";
  const when = new Date(a.created_at);
  const friendly = when.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  return (
    <div className="flex items-center gap-3 px-3 py-2 bg-zinc-950/40 border border-zinc-800/60 rounded-md text-xs">
      <Clock className="w-3 h-3 text-zinc-600 shrink-0" />
      <span className="text-zinc-500 w-40 shrink-0">{friendly}</span>
      <span className={`uppercase tracking-wider font-medium w-28 shrink-0 ${color}`}>{a.action}</span>
      <span className="text-zinc-300 flex-1 truncate">{a.target_label || a.target_id}</span>
      <span className="text-zinc-500 text-[10px]">by {a.actor_name || a.actor_email}</span>
    </div>
  );
}

// Iter 45 — Compact brand assignment picker used in both the Team and Clients rows.
// Sends `PATCH /api/admin/users/{id}/brand`. Optimistic UI: swap the pill's color
// dot immediately, revert on error. Sonner toast on success/failure.
// Iter 60 — When the admin reassigns their OWN brand, refresh the AuthContext
// so BrandContext instantly re-derives colors + logo (kills the "sticky"
// dropdown symptom where the theme required a page reload to switch).
function BrandSelect({ userId, currentBrand, onChanged }) {
  const { user: authUser, refreshUser } = useAuth();
  const [brand, setBrand] = useState(currentBrand || "whiplash");
  const [saving, setSaving] = useState(false);
  const cfg = BRANDS[brand] || BRANDS.whiplash;

  const change = async (next) => {
    if (next === brand) return;
    const prev = brand;
    setBrand(next);
    setSaving(true);
    try {
      await api.patch(`/admin/users/${userId}/brand`, { brand: next });
      toast.success(`Brand → ${BRANDS[next]?.short || next}`);
      // If we just repainted the currently-logged-in user's brand, pull
      // /auth/me so BrandContext re-derives immediately.
      if (authUser?.id === userId && typeof refreshUser === "function") {
        await refreshUser();
      }
      onChanged?.();
    } catch (e) {
      setBrand(prev);
      toast.error(e.response?.data?.detail || "Could not update brand");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative shrink-0" data-testid={`brand-select-${userId}`}>
      <span
        aria-hidden
        className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full"
        style={{ background: cfg.accent }}
      />
      {saving && (
        <Loader2 className="pointer-events-none absolute right-6 top-1/2 -translate-y-1/2 w-3 h-3 animate-spin text-[var(--brand-accent)]" />
      )}
      <select
        value={brand}
        onChange={(e) => change(e.target.value)}
        disabled={saving}
        className="appearance-none bg-zinc-950 border border-zinc-800 hover:border-[var(--brand-accent)]/40 rounded-full text-[10px] uppercase tracking-wider text-zinc-300 pl-6 pr-6 py-1 focus:outline-none focus:border-[var(--brand-accent)]/60 disabled:opacity-60 cursor-pointer"
        title="Change brand theme"
      >
        {Object.values(BRANDS).map((b) => (
          <option key={b.id} value={b.id}>{b.short}</option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-500" />
    </div>
  );
}
